import React from "react";
import { observer } from "mobx-react-lite";
import type CreditExposuresViewModel from "viewModels/timeCharges/distributors/creditExposures/creditExposuresViewModel";
import { Col, Row } from "react-bootstrap";
import ExposureCard from "./exposureCard";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import CreditExposureNotificationStatus from "entities/creditExposureNotificationStatus";
import classNames from "classnames";

export interface CreditExposureCardsProps {
  vm: CreditExposuresViewModel;
}

const CreditExposureCards = observer((props: CreditExposureCardsProps) => {
  const { vm } = props;

  // After editing exposures, the notification is not updated immediately.
  // This is why it's necessary to manually calculate insufficient credit exposure.
  const creditExposureAmountClassNames = classNames("mb-4", {
    "below-notification-limit": vm.isCreditExposureAmountInsufficient,
    "exhausted-limit": vm.isCreditExposureAmountExhausted,
  });

  const notificationClassNames = classNames("mb-4", {
    "below-notification-limit":
      vm.currentCreditExposure?.warningNotificationStatus === CreditExposureNotificationStatus.SentWarning,
    "exhausted-limit": vm.currentCreditExposure?.warningNotificationStatus === CreditExposureNotificationStatus.SentExhausted,
  });

  return (
    <LoaderOverlay loading={vm.busyWatcher.isBusy}>
      {vm.currentCreditExposure && (
        <>
          <Row className="pb-2">
            <Col className="mb-4">
              <ExposureCard label={vm.translate("totalExposure")} amount={vm.totalExposure} />
            </Col>
            <Col className="mb-4">
              <ExposureCard label={vm.translate("reservedExposure")} amount={vm.reservedExposure} />
            </Col>
            <Col className={creditExposureAmountClassNames}>
              <ExposureCard label={vm.translate("remainingExposure")} amount={vm.remainingExposure} />
            </Col>
          </Row>
          <Row className="pb-4">
            <Col className={creditExposureAmountClassNames}>
              <ExposureCard label={vm.translate("usedExposurePercentage")} amount={vm.usedExposurePercentage} />
            </Col>
            <Col className="mb-4">
              <ExposureCard label={vm.translate("creditExposureWarningPercentage")} amount={vm.creditExposureWarningPercentage} />
            </Col>
            <Col className={notificationClassNames}>
              <ExposureCard
                label={vm.translate("creditExposureNotificationStatus")}
                amount={vm.creditExposureNotificationStatus}
              />
            </Col>
          </Row>
        </>
      )}
    </LoaderOverlay>
  );
});

export default CreditExposureCards;
